// realtime-detector.js - 실시간 문서 디텍션 루프

import { detectCornersFromVideo, isDocAlignerReady, loadDocAlignerModel } from './docaligner-inference.js';
import { StabilityTracker, StabilityState } from './stability-tracker.js';
import { CornerOverlay } from './corner-overlay.js';

/**
 * 설정 상수
 */
const MAX_INFERENCE_TIME = 80;  // 이 시간 초과 시 프레임 스킵 고려

// 적응형 FPS: 상태별 프레임 간격 (ms)
const STATE_INTERVALS = {
  not_detected: 1000 / 10,  // 10fps: 적극적 탐색
  unstable:     1000 / 8,   // 8fps: 문서 발견, 추적 중
  stabilizing:  1000 / 5,   // 5fps: 수렴 중, 부하 감소
  stable:       1000 / 2,   // 2fps: 안정 확인만 (바로 auto-capture됨)
};
const DEFAULT_INTERVAL = 1000 / 10;

// Idle 감지: 미감지 지속 시 추론 일시정지
const IDLE_TIMEOUT = 5000;              // 5초 미감지 → idle 모드
const IDLE_CHECK_INTERVAL = 500;        // idle 모드에서 모션 체크 간격 (ms)
const MOTION_CANVAS_SIZE = 32;          // 모션 감지용 축소 해상도
const MOTION_THRESHOLD = 15;            // 픽셀 평균 변화량 임계치 (0-255)

/**
 * 실시간 디텍터 클래스
 */
export class RealtimeDetector {
  constructor(videoElement, overlayCanvas) {
    this.video = videoElement;
    this.overlay = new CornerOverlay(overlayCanvas, videoElement);
    this.tracker = new StabilityTracker();

    // 상태
    this.isRunning = false;
    this.loopTimeoutId = null;
    this.frameCount = 0;
    this.skipNextFrame = false;

    // Idle 모드 (미감지 5초 후 추론 중단, 모션 감지로 재개)
    this.idleMode = false;
    this.notDetectedSince = 0;       // NOT_DETECTED 시작 시각
    this.motionCanvas = null;
    this.motionCtx = null;
    this.prevMotionData = null;

    // 통계
    this.stats = {
      fps: 0,
      avgInferenceTime: 0,
      inferenceCount: 0
    };

    // 콜백
    this.onAutoCapture = null;        // 자동 캡처 트리거
    this.onStateChange = null;        // 상태 변경
    this.onError = null;              // 에러 발생

    // 안정화 콜백 연결
    this.tracker.onStable = (corners, confidence) => {
      this.handleStable(corners, confidence);
    };

    this.tracker.onStabilityChange = (state, corners) => {
      if (this.onStateChange) {
        this.onStateChange(state, corners);
      }
    };
  }

  /**
   * 디텍션 시작
   */
  async start() {
    if (this.isRunning) return;

    // DocAligner 모델 로드 확인
    if (!isDocAlignerReady()) {
      try {
        await loadDocAlignerModel();
      } catch (e) {
        console.error('Failed to load DocAligner for realtime detection:', e);
        if (this.onError) {
          this.onError(e);
        }
        return;
      }
    }

    this.isRunning = true;
    this.frameCount = 0;
    this.idleMode = false;
    this.notDetectedSince = 0;
    this.prevMotionData = null;
    this.tracker.reset();
    this.overlay.reset();

    console.log('Realtime detector started');
    this.runLoop();
  }

  /**
   * 디텍션 중지
   */
  stop() {
    this.isRunning = false;
    if (this.loopTimeoutId) {
      clearTimeout(this.loopTimeoutId);
      this.loopTimeoutId = null;
    }

    this.tracker.reset();
    this.overlay.reset();
    console.log('Realtime detector stopped');
  }

  /**
   * 현재 상태에 따른 프레임 간격 반환 (적응형 FPS)
   */
  getFrameInterval() {
    if (this.idleMode) return IDLE_CHECK_INTERVAL;
    const state = this.tracker.getState();
    return STATE_INTERVALS[state] || DEFAULT_INTERVAL;
  }

  /**
   * 메인 루프
   * setTimeout 사용으로 requestAnimationFrame Violation 방지
   */
  runLoop() {
    if (!this.isRunning) return;

    const interval = this.getFrameInterval();

    this.loopTimeoutId = setTimeout(async () => {
      if (!this.isRunning) return;

      if (this.idleMode) {
        // Idle 모드: 가벼운 모션 감지만 수행
        if (this.detectMotion()) {
          this.idleMode = false;
          this.notDetectedSince = 0;
          this.prevMotionData = null;
          console.log('Motion detected, resuming detection');
        }
      } else if (!this.skipNextFrame) {
        await this.processFrame();
        this.checkIdleTransition();
      } else {
        this.skipNextFrame = false;
      }

      // 다음 프레임 예약
      this.runLoop();
    }, interval);
  }

  /**
   * NOT_DETECTED 상태가 IDLE_TIMEOUT 이상 지속되면 idle 모드 전환
   */
  checkIdleTransition() {
    const state = this.tracker.getState();
    if (state === StabilityState.NOT_DETECTED) {
      if (this.notDetectedSince === 0) {
        this.notDetectedSince = Date.now();
      } else if (Date.now() - this.notDetectedSince >= IDLE_TIMEOUT) {
        this.idleMode = true;
        this.overlay.reset();
        console.log('No document for 5s, entering idle mode');
      }
    } else {
      this.notDetectedSince = 0;
    }
  }

  /**
   * 경량 모션 감지 (idle 모드에서 사용)
   * 비디오 프레임을 32x32로 축소 후 이전 프레임과 픽셀 차이 비교
   * @returns {boolean} 모션 감지 여부
   */
  detectMotion() {
    if (!this.video || !this.video.videoWidth || this.video.paused) return false;

    if (!this.motionCanvas) {
      this.motionCanvas = document.createElement('canvas');
      this.motionCanvas.width = MOTION_CANVAS_SIZE;
      this.motionCanvas.height = MOTION_CANVAS_SIZE;
      this.motionCtx = this.motionCanvas.getContext('2d', { willReadFrequently: true, alpha: false });
    }

    this.motionCtx.drawImage(this.video, 0, 0, MOTION_CANVAS_SIZE, MOTION_CANVAS_SIZE);
    const imageData = this.motionCtx.getImageData(0, 0, MOTION_CANVAS_SIZE, MOTION_CANVAS_SIZE);
    const data = imageData.data;

    if (!this.prevMotionData) {
      this.prevMotionData = new Uint8Array(data);
      return false;
    }

    // 픽셀 차이 합산 (R 채널만 비교 — 충분히 빠르고 정확)
    let totalDiff = 0;
    const pixelCount = MOTION_CANVAS_SIZE * MOTION_CANVAS_SIZE;
    for (let i = 0; i < data.length; i += 4) {
      totalDiff += Math.abs(data[i] - this.prevMotionData[i]);
    }

    // 현재 프레임 저장
    this.prevMotionData.set(data);

    const avgDiff = totalDiff / pixelCount;
    return avgDiff > MOTION_THRESHOLD;
  }

  /**
   * 프레임 처리
   */
  async processFrame() {
    if (!this.video || !this.video.videoWidth || this.video.paused) {
      // 비디오가 준비되지 않음
      this.overlay.render(null, StabilityState.NOT_DETECTED, 0);
      return;
    }

    try {
      // DocAligner 추론
      const { corners, confidence, minConfidence, inferenceTime } = await detectCornersFromVideo(this.video);

      // 통계 업데이트
      this.updateStats(inferenceTime);

      // 추론 시간이 너무 길면 다음 프레임 스킵
      if (inferenceTime > MAX_INFERENCE_TIME) {
        this.skipNextFrame = true;
      }

      // 안정화 추적기에 프레임 추가
      const state = this.tracker.addFrame(corners, confidence);
      const progress = this.tracker.getProgress();

      // 오버레이 렌더링 (스무딩된 코너 사용)
      const smoothedCorners = this.tracker.getSmoothedCorners(3);
      this.overlay.render(smoothedCorners || corners, state, progress);

      this.frameCount++;

    } catch (e) {
      console.error('Frame processing error:', e);
      this.overlay.render(null, StabilityState.NOT_DETECTED, 0);
    }
  }

  /**
   * 안정 상태 도달 처리
   */
  handleStable(corners, confidence) {
    console.log('Document stable, triggering auto capture');

    // 디텍션 루프 중지 (비디오는 아직 프리즈 안 함)
    this.stop();

    // 햅틱 피드백
    try { navigator.vibrate?.([50, 30, 50]); } catch {}

    // 자동 캡처 콜백에 코너 정보 전달
    if (this.onAutoCapture) {
      this.onAutoCapture(corners, confidence);
    }
  }

  /**
   * 현재 코너 오버레이 상태 유지 (프리즈용)
   */
  freezeOverlay() {
    // 오버레이는 그대로 두고 업데이트만 중지
    // (이미 stop()에서 루프가 멈춤)
  }

  /**
   * 통계 업데이트
   */
  updateStats(inferenceTime) {
    this.stats.inferenceCount++;

    // 이동 평균
    const alpha = 0.1;
    this.stats.avgInferenceTime = this.stats.avgInferenceTime * (1 - alpha) + inferenceTime * alpha;

    // FPS 계산 (10프레임마다)
    if (this.stats.inferenceCount % 10 === 0) {
      const currentInterval = this.getFrameInterval();
      this.stats.fps = Math.round(1000 / (this.stats.avgInferenceTime + currentInterval / 2));
    }
  }

  /**
   * 현재 상태 조회
   */
  getState() {
    return this.tracker.getState();
  }

  /**
   * 통계 조회
   */
  getStats() {
    return { ...this.stats };
  }

  /**
   * 수동 리셋 (상태 초기화)
   */
  reset() {
    this.tracker.reset();
    this.overlay.reset();
  }

  /**
   * 리소스 정리
   */
  destroy() {
    this.stop();
    this.onAutoCapture = null;
    this.onStateChange = null;
    this.onError = null;
    this.motionCanvas = null;
    this.motionCtx = null;
    this.prevMotionData = null;
  }
}

/**
 * 실시간 디텍터 인스턴스 생성 헬퍼
 */
export function createRealtimeDetector(videoElement, overlayCanvas) {
  return new RealtimeDetector(videoElement, overlayCanvas);
}
